// Browser tracing. Loaded first from src/index.js so that instrumentation is
// patched in before React, the router, or any fetch call exists.
//
// NO BUILD-TIME CONFIGURATION. The exporter posts to a ROOT-RELATIVE path and
// the environment is read from the hostname at runtime, so one image is
// correct on dev-bex, stg-bex, and any future host. A build-time variable
// would mean the staging image is not the artifact that was tested.
import { WebTracerProvider, BatchSpanProcessor, TraceIdRatioBasedSampler } from '@opentelemetry/sdk-trace-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { resourceFromAttributes } from '@opentelemetry/resources';

const SERVICE_NAME = 'bex-platform-react';

function environmentOf(hostname) {
    if (hostname.startsWith('stg-')) return { env: 'staging', ratio: 0.1 };
    if (hostname.startsWith('dev-') || hostname === 'localhost') return { env: 'development', ratio: 1.0 };
    return { env: 'unknown', ratio: 0.1 };
}

export function startTelemetry() {
    try {
        const { env, ratio } = environmentOf(window.location.hostname);

        const provider = new WebTracerProvider({
            resource: resourceFromAttributes({
                'service.name': SERVICE_NAME,
                'deployment.environment': env,
            }),
            // TraceIdRatioBasedSampler, not a hand-rolled shouldSample. A
            // sampler returning { decision: 1 } looks correct and silently
            // exports nothing: in OpenTelemetry, 1 is RECORD — build the span,
            // do NOT send it — and only 2 is RECORD_AND_SAMPLED. The spans get
            // created, isRecording() is false, traceFlags stay 0, and the
            // exporter is never called. Nothing errors.
            sampler: new TraceIdRatioBasedSampler(ratio),
            spanProcessors: [
                new BatchSpanProcessor(
                    new OTLPTraceExporter({ url: '/api/telemetry/v1/traces' }),
                    { scheduledDelayMillis: 5000, maxExportBatchSize: 32 },
                ),
            ],
        });

        provider.register({ contextManager: new ZoneContextManager() });

        registerInstrumentations({
            instrumentations: [
                getWebAutoInstrumentations({
                    // Every click and keypress becomes a span otherwise, which
                    // buries the request traces this exists to produce.
                    '@opentelemetry/instrumentation-user-interaction': { enabled: false },
                }),
            ],
        });
    } catch (err) {
        // Telemetry must never keep the app from rendering.
        // eslint-disable-next-line no-console
        console.warn('[telemetry] disabled:', err);
    }
}
